// Choferes asignados a cada tienda para el Turbo (store_turbo_drivers). Un
// turbo de la tienda se les ofrece primero a ellos, de a uno y en el orden de
// esta lista, antes que a los más cercanos. Se elige la tienda, se arma la
// lista (agregar, subir/bajar, quitar) y se guarda entera.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";

type Req = (path: string, init?: RequestInit) => Promise<any>;
type Store = { id: number; nombre_fantasia: string | null; active?: boolean | null };
type Driver = { id: number; username: string; first_name: string | null; last_name: string | null; vehiculo?: string | null };
type Assigned = { driver_id: number; priority: number; username: string; first_name: string | null; last_name: string | null; vehiculo: string | null; turbo_enabled: boolean; active: boolean };
type Summary = { store_id: number; store_name: string | null; drivers: Assigned[] };

const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "8px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "middle" };
const selectStyle: React.CSSProperties = { minHeight: "40px", padding: "0 10px", borderRadius: "12px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)", font: "inherit", minWidth: "240px", maxWidth: "100%" };
const WARN = "var(--warning, #d29922)";

const driverName = (d: { username: string; first_name?: string | null; last_name?: string | null }) => [d.first_name, d.last_name].filter(Boolean).join(" ") || d.username;
const storeName = (s: { nombre_fantasia?: string | null; store_name?: string | null; id?: number; store_id?: number }) => s.nombre_fantasia || s.store_name || `Tienda ${s.id ?? s.store_id}`;

export default function StoreTurboDriversCard({ req }: { req: Req }) {
  const [stores, setStores] = useState<Store[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [summary, setSummary] = useState<Summary[]>([]);
  const [filter, setFilter] = useState("");
  const [storeId, setStoreId] = useState<number | null>(null);
  const [list, setList] = useState<Assigned[]>([]);
  const [toAdd, setToAdd] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    const r = await req("/v2/turbo/store-drivers");
    setSummary(r.stores ?? []);
  }, [req]);

  useEffect(() => {
    Promise.all([req("/v2/stores"), req("/v2/routes/drivers"), loadSummary()])
      .then(([s, d]) => {
        setStores((s.stores ?? []).filter((x: Store) => x.active !== false).sort((a: Store, b: Store) => storeName(a).localeCompare(storeName(b))));
        setDrivers(d.drivers ?? []);
      })
      .catch((e) => setError(e.message));
  }, [req, loadSummary]);

  const pickStore = async (id: number | null, { force = false } = {}) => {
    if (dirty && !force && !window.confirm("Hay cambios sin guardar en esta tienda. ¿Descartarlos?")) return;
    setStoreId(id); setList([]); setDirty(false); setToAdd(""); setError(null);
    if (!id) return;
    try {
      const r = await req(`/v2/turbo/store-drivers/${id}`);
      setList(r.drivers ?? []);
    } catch (e: any) { setError(e.message); }
  };

  // La tienda elegida siempre queda en la lista, aunque el filtro no la incluya.
  const visibleStores = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? stores.filter((s) => s.id === storeId || storeName(s).toLowerCase().includes(q) || String(s.id) === q) : stores;
  }, [stores, filter, storeId]);
  const addable = useMemo(() => drivers.filter((d) => !list.some((a) => a.driver_id === d.id)), [drivers, list]);

  const add = () => {
    const d = drivers.find((x) => String(x.id) === toAdd);
    if (!d) return;
    // turbo_enabled no viene en la lista de choferes: se sabrá al guardar.
    setList((l) => [...l, { driver_id: d.id, priority: l.length + 1, username: d.username, first_name: d.first_name, last_name: d.last_name, vehiculo: d.vehiculo ?? null, turbo_enabled: true, active: true }]);
    setToAdd(""); setDirty(true);
  };
  const move = (i: number, delta: number) => {
    setList((l) => {
      const j = i + delta;
      if (j < 0 || j >= l.length) return l;
      const next = [...l];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };
  const remove = (i: number) => { setList((l) => l.filter((_, k) => k !== i)); setDirty(true); };

  const save = async () => {
    if (!storeId) return;
    setSaving(true); setError(null);
    try {
      const r = await req(`/v2/turbo/store-drivers/${storeId}`, { method: "PUT", body: JSON.stringify({ driver_ids: list.map((a) => a.driver_id) }) });
      setList(r.drivers ?? []);
      setDirty(false);
      await loadSummary();
      setNotice("Choferes de la tienda guardados");
      window.setTimeout(() => setNotice(null), 4000);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: "16px", color: "var(--text-primary)" }}>Choferes asignados por tienda</h3>
      <p style={{ ...smallMutedStyle, margin: "0 0 14px" }}>
        Un turbo de la tienda se le ofrece primero al chofer #1 (si está dentro del radio del asignado); si no contesta o no puede, al #2, y así. Agotada la lista, se ofrece a los más cercanos como siempre.
        El chofer tiene que estar habilitado para Turbo y con el interruptor prendido.
      </p>

      {error ? <div style={{ color: "var(--danger)", marginBottom: "10px" }}>{error}</div> : null}
      {notice ? <div style={{ color: "var(--success)", marginBottom: "10px" }}>{notice}</div> : null}

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
        <input id="std-filter" style={selectStyle} placeholder="Buscar tienda…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <select id="std-store" style={selectStyle} value={storeId ?? ""} onChange={(e) => pickStore(e.target.value ? Number(e.target.value) : null)}>
          <option value="">Elegir tienda ({visibleStores.length})</option>
          {visibleStores.map((s) => <option key={s.id} value={s.id}>{storeName(s)}</option>)}
        </select>
      </div>

      {storeId ? (
        <div style={{ marginTop: "14px" }}>
          {list.length ? (
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Orden</th><th style={thStyle}>Chofer</th><th style={thStyle}>Vehículo</th><th style={thStyle}></th></tr></thead>
                <tbody>
                  {list.map((a, i) => (
                    <tr key={a.driver_id}>
                      <td style={tdStyle}>#{i + 1}</td>
                      <td style={tdStyle}>
                        {driverName(a)}<div style={smallMutedStyle}>{a.username}</div>
                        {!a.turbo_enabled ? <div style={{ ...smallMutedStyle, color: WARN }}>no está habilitado para Turbo: se lo saltea</div> : null}
                        {!a.active ? <div style={{ ...smallMutedStyle, color: WARN }}>chofer inactivo: se lo saltea</div> : null}
                      </td>
                      <td style={tdStyle}>{a.vehiculo ?? "—"}</td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        <Button variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
                        <Button variant="ghost" onClick={() => move(i, 1)} disabled={i === list.length - 1}>↓</Button>
                        <Button variant="ghost" onClick={() => remove(i)}>Quitar</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={smallMutedStyle}>Esta tienda no tiene choferes asignados: sus turbos van directo a los más cercanos.</div>}

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginTop: "12px" }}>
            <select id="std-add" style={selectStyle} value={toAdd} onChange={(e) => setToAdd(e.target.value)}>
              <option value="">Agregar chofer…</option>
              {addable.map((d) => <option key={d.id} value={d.id}>{driverName(d)} ({d.username}){d.vehiculo ? ` · ${d.vehiculo}` : ""}</option>)}
            </select>
            <Button variant="secondary" onClick={add} disabled={!toAdd}>Agregar</Button>
            <Button onClick={save} disabled={!dirty || saving}>{saving ? "Guardando…" : "Guardar"}</Button>
            <Button variant="secondary" onClick={() => pickStore(storeId, { force: true })} disabled={!dirty || saving}>Descartar</Button>
          </div>
        </div>
      ) : null}

      <h4 style={{ margin: "20px 0 6px", fontSize: "14px", color: "var(--text-primary)" }}>Tiendas con choferes asignados ({summary.length})</h4>
      {summary.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Tienda</th><th style={thStyle}>Choferes, en orden</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {summary.map((s) => (
                <tr key={s.store_id}>
                  <td style={tdStyle}>{storeName(s)}</td>
                  <td style={tdStyle}>{s.drivers.map((d, i) => `${i + 1}. ${driverName(d)}`).join(" · ")}</td>
                  <td style={tdStyle}><Button variant="ghost" onClick={() => pickStore(s.store_id)}>Editar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div style={smallMutedStyle}>Ninguna todavía.</div>}
    </Card>
  );
}
